/**
 * The field DSL. One definition per field drives validation (zod), the agent's tool schema
 * (JSON Schema) and the generated edit form. Labels are English catalog keys; the web
 * translates them with t(). Keep this file isomorphic: no DOM and no Worker APIs.
 */
import { z } from "zod";
import { UNITS, type Dimension } from "./units";

export type RefTarget =
  | "animal" | "group" | "place" | "contact" | "stock_item" | "stock_lot" | "asset" | "job" | "member" | "fin_doc";

/** core: shown in the first view of a form. more: behind "More details". */
export type Level = "core" | "more";

interface Base<K extends string> {
  kind: K;
  label: string;
  help?: string;
  required?: boolean;
  level?: Level;
  /** Extra instruction for the agent, never shown to people. */
  agent?: string;
  placeholder?: string;
}

export type TextField = Base<"text"> & { multiline?: boolean; max?: number };
export type TagField = Base<"tag">;
export type NumberField = Base<"number"> & { min?: number; max?: number; integer?: boolean; suffix?: string; step?: number };
export type BooleanField = Base<"boolean">;
export type DateField = Base<"date"> & { withTime?: boolean };
export type EnumField = Base<"enum"> & { options: readonly string[]; optionLabels: Record<string, string>; multiple?: boolean };
export type QuantityField = Base<"quantity"> & { dimension: Dimension; units?: readonly string[] };
export type MoneyField = Base<"money">;
export type RefField = Base<"ref"> & { target: RefTarget; multiple?: boolean; filter?: Record<string, string | string[]> };
export type ObjectField = Base<"object"> & { fields: Fields };
export type ListField = Base<"list"> & { of: Field; max?: number; itemLabel?: string };

export type Field =
  | TextField | TagField | NumberField | BooleanField | DateField | EnumField
  | QuantityField | MoneyField | RefField | ObjectField | ListField;
export type Fields = Record<string, Field>;

export interface Quantity { value: number; unit: string }
export interface Money { minor: number; currency: string }

type Opts<F> = Omit<F, "kind" | "label" | "options" | "optionLabels" | "dimension" | "target" | "fields" | "of">;

/** Builders. `f.enum("Outcome", { alive: "Alive", born_dead: "Born dead" })` keeps values stable and labels translatable. */
export const f = {
  text: (label: string, o: Opts<TextField> = {}): TextField => ({ kind: "text", label, ...o }),
  longText: (label: string, o: Opts<TextField> = {}): TextField => ({ kind: "text", label, multiline: true, ...o }),
  tag: (label: string, o: Opts<TagField> = {}): TagField => ({ kind: "tag", label, ...o }),
  number: (label: string, o: Opts<NumberField> = {}): NumberField => ({ kind: "number", label, ...o }),
  int: (label: string, o: Opts<NumberField> = {}): NumberField => ({ kind: "number", label, integer: true, ...o }),
  bool: (label: string, o: Opts<BooleanField> = {}): BooleanField => ({ kind: "boolean", label, ...o }),
  date: (label: string, o: Opts<DateField> = {}): DateField => ({ kind: "date", label, ...o }),
  datetime: (label: string, o: Opts<DateField> = {}): DateField => ({ kind: "date", label, withTime: true, ...o }),
  enum: <T extends string>(label: string, options: Record<T, string>, o: Opts<EnumField> = {}): EnumField =>
    ({ kind: "enum", label, options: Object.keys(options), optionLabels: options, ...o }),
  multiEnum: <T extends string>(label: string, options: Record<T, string>, o: Opts<EnumField> = {}): EnumField =>
    ({ kind: "enum", label, options: Object.keys(options), optionLabels: options, multiple: true, ...o }),
  quantity: (label: string, dimension: Dimension, o: Opts<QuantityField> = {}): QuantityField => ({ kind: "quantity", label, dimension, ...o }),
  money: (label: string, o: Opts<MoneyField> = {}): MoneyField => ({ kind: "money", label, ...o }),
  ref: (label: string, target: RefTarget, o: Opts<RefField> = {}): RefField => ({ kind: "ref", label, target, ...o }),
  refs: (label: string, target: RefTarget, o: Opts<RefField> = {}): RefField => ({ kind: "ref", label, target, multiple: true, ...o }),
  object: (label: string, fields: Fields, o: Opts<ObjectField> = {}): ObjectField => ({ kind: "object", label, fields, ...o }),
  list: (label: string, of: Field, o: Opts<ListField> = {}): ListField => ({ kind: "list", label, of, ...o }),
};

// ---------------------------------------------------------------- validation

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date");
const isoDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/, "datetime");

function baseZod(field: Field): z.ZodType {
  switch (field.kind) {
    case "text": return z.string().max(field.max ?? (field.multiline ? 20000 : 500));
    case "tag": return z.string().min(1).max(40);
    case "number": {
      let n = z.number().finite();
      if (field.integer) n = n.int();
      if (field.min !== undefined) n = n.min(field.min);
      if (field.max !== undefined) n = n.max(field.max);
      return n;
    }
    case "boolean": return z.boolean();
    case "date": return field.withTime ? isoDateTime : z.union([isoDate, isoDateTime]);
    case "enum": {
      const one = z.enum(field.options as [string, ...string[]]);
      return field.multiple ? z.array(one) : one;
    }
    case "quantity": return z.object({ value: z.number().finite(), unit: z.string().min(1).max(24) });
    case "money": return z.object({ minor: z.number().int(), currency: z.string().length(3) });
    case "ref": return field.multiple ? z.array(z.string().min(1)) : z.string().min(1);
    case "object": return zodFor(field.fields);
    case "list": return z.array(fieldZod({ ...field.of, required: true })).max(field.max ?? 500);
  }
}

export function fieldZod(field: Field): z.ZodType {
  const base = baseZod(field);
  return field.required ? base : base.nullish();
}

/** Strict object schema: unknown keys are rejected so typos cannot silently drop facts. */
export function zodFor(fields: Fields): z.ZodObject {
  return z.object(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, fieldZod(v)]))).strict();
}

export interface Issue { path: string; message: string; code?: string }

export function validateFields(fields: Fields, data: unknown): { ok: true; data: Record<string, unknown> } | { ok: false; issues: Issue[] } {
  const parsed = zodFor(fields).safeParse(stripNulls(data));
  if (parsed.success) return { ok: true, data: stripNulls(parsed.data) as Record<string, unknown> };
  return { ok: false, issues: parsed.error.issues.map((i) => ({ path: i.path.join("."), message: i.message, code: i.code })) };
}

/** Missing facts are omitted rather than stored as null, empty string or zero. */
export function stripNulls<T>(value: T): T {
  if (Array.isArray(value)) return value.map(stripNulls) as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === null || v === undefined || v === "") continue;
      out[k] = stripNulls(v);
    }
    return out as T;
  }
  return value;
}

// ---------------------------------------------------------------- agent JSON Schema

type Json = Record<string, unknown>;

function describe(field: Field): string {
  return [field.label, field.help, field.agent].filter(Boolean).join(". ");
}

export function fieldJsonSchema(field: Field): Json {
  const description = describe(field);
  switch (field.kind) {
    case "text": return { type: "string", description };
    case "tag": return { type: "string", description: `${description}. Ear tag exactly as spoken or written, e.g. 42, 915F29, Y-12.` };
    case "number": return { type: field.integer ? "integer" : "number", description, ...(field.min !== undefined ? { minimum: field.min } : {}), ...(field.max !== undefined ? { maximum: field.max } : {}) };
    case "boolean": return { type: "boolean", description };
    case "date": return { type: "string", description: `${description}. ${field.withTime ? "ISO date-time" : "ISO date YYYY-MM-DD"}` };
    case "enum": {
      const values = { type: "string", enum: field.options };
      return field.multiple ? { type: "array", items: values, description } : { ...values, description };
    }
    case "quantity": {
      const units = field.units ?? UNITS[field.dimension];
      return {
        type: "object", description,
        properties: { value: { type: "number" }, unit: units.length ? { type: "string", enum: [...units] } : { type: "string" } },
        required: ["value", "unit"], additionalProperties: false,
      };
    }
    case "money": return {
      type: "object", description: `${description}. Amount in the currency's normal units (dollars, not cents).`,
      properties: { amount: { type: "number" }, currency: { type: "string", description: "ISO code, default USD" } },
      required: ["amount"], additionalProperties: false,
    };
    case "ref": {
      const one = { type: "string", description: `${field.target} id from a lookup tool, or new:<temp_id> for something created in this proposal` };
      return field.multiple ? { type: "array", items: one, description } : { ...one, description: `${description}. ${one.description}` };
    }
    case "object": return { ...jsonSchemaFor(field.fields), description };
    case "list": return { type: "array", items: fieldJsonSchema(field.of), description };
  }
}

export function jsonSchemaFor(fields: Fields): Json {
  return {
    type: "object",
    properties: Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, fieldJsonSchema(v)])),
    required: Object.entries(fields).filter(([, v]) => v.required).map(([k]) => k),
    additionalProperties: false,
  };
}

/** Convert agent-shaped values (money as decimal amounts) into stored shapes. */
export function fromAgent(fields: Fields, data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object") return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const field = fields[key];
    if (!field) { out[key] = value; continue; }
    out[key] = convert(field, value);
  }
  return out;
}

function convert(field: Field, value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (field.kind === "money" && typeof value === "object") {
    const v = value as { amount?: number; minor?: number; currency?: string };
    if (typeof v.minor === "number") return { minor: Math.round(v.minor), currency: (v.currency ?? "USD").toUpperCase() };
    if (typeof v.amount === "number") return { minor: Math.round(v.amount * 100), currency: (v.currency ?? "USD").toUpperCase() };
  }
  if (field.kind === "object") return fromAgent(field.fields, value);
  if (field.kind === "list" && Array.isArray(value)) return value.map((item) => convert(field.of, item));
  if (field.kind === "number" && typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
  return value;
}

// ---------------------------------------------------------------- walking

/** Every ref id inside a payload, for label lookups and integrity checks. */
export function collectRefs(fields: Fields, data: unknown, out: Array<{ target: RefTarget; id: string; path: string }> = [], prefix = ""): typeof out {
  if (!data || typeof data !== "object") return out;
  for (const [key, field] of Object.entries(fields)) {
    const value = (data as Record<string, unknown>)[key];
    if (value === undefined || value === null) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (field.kind === "ref") {
      for (const id of Array.isArray(value) ? value : [value]) if (typeof id === "string") out.push({ target: field.target, id, path });
    } else if (field.kind === "object") collectRefs(field.fields, value, out, path);
    else if (field.kind === "list" && Array.isArray(value)) {
      value.forEach((item, i) => {
        if (field.of.kind === "object") collectRefs(field.of.fields, item, out, `${path}.${i}`);
        else if (field.of.kind === "ref" && typeof item === "string") out.push({ target: field.of.target, id: item, path: `${path}.${i}` });
      });
    }
  }
  return out;
}

/** Replace temporary ids (new:calf1) with committed ids throughout a payload. */
export function replaceRefs<T>(value: T, map: Record<string, string>): T {
  if (typeof value === "string") return (map[value] ?? value) as T;
  if (Array.isArray(value)) return value.map((v) => replaceRefs(v, map)) as T;
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, replaceRefs(v, map)])) as T;
  return value;
}

/** Remove every money value from a payload (prices, deposits, costs) for people without finance access. */
export function redactMoney(fields: Fields, data: Record<string, unknown>): { data: Record<string, unknown>; redacted: boolean } {
  let redacted = false;
  const walk = (fs: Fields, value: unknown): unknown => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return value;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const field = fs[k];
      if (field?.kind === "money") { redacted = true; continue; }
      if (field?.kind === "object") out[k] = walk(field.fields, v);
      else if (field?.kind === "list" && field.of.kind === "object" && Array.isArray(v)) out[k] = v.map((item) => walk((field.of as ObjectField).fields, item));
      else out[k] = v;
    }
    return out;
  };
  return { data: walk(fields, data) as Record<string, unknown>, redacted };
}

/** All human-facing strings in a field tree, for the localization check. */
export function fieldLabels(fields: Fields, out: Set<string> = new Set()): Set<string> {
  for (const field of Object.values(fields)) {
    out.add(field.label);
    if (field.help) out.add(field.help);
    if (field.placeholder) out.add(field.placeholder);
    if (field.kind === "enum") for (const label of Object.values(field.optionLabels)) out.add(label);
    if (field.kind === "object") fieldLabels(field.fields, out);
    if (field.kind === "list") {
      if (field.itemLabel) out.add(field.itemLabel);
      fieldLabels({ of: field.of }, out);
    }
  }
  return out;
}
